import {
  Control,
  FieldErrors,
  UseFormRegister,
  UseFormSetError,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";

import {
  NamesAndEmailSchemaType,
  TermsAndConditionsSchemaType,
} from "~/common/utils/signUpSchemas";
import { PATIENT_SIGN_UP_ADULT_AGE } from "~/patients/constants";
import { useOnBoardingNewPatientStore } from "~/patients/stores/useOnBoardingNewPatientStore";
import {
  AddressSchemaType,
  isOlderThanYears,
  NewPatientSchemaType,
  SignUpPatientSchemaType,
} from "~/patients/utils";
import PatientAddressForm from "../commonForms/PatientAddressForm";
import PatientMobileForm from "../commonForms/PatientMobileForm";
import { PatientMobileVerificationForm } from "../commonForms/PatientMobileVerificationForm";
import PatientPasswordForm, {
  PasswordsSchemaType,
} from "../commonForms/PatientPasswordForm";
import { NewPatientBirthdateForm } from "./NewPatientBirthdateForm";
import { NewPatientNameAndEmailForm } from "./NewPatientNameAndEmailForm";
import { NewPatientTermsAndConditionsForm } from "./NewPatientTermsAndConditionsForm";

export const STEPS_LENGTH = 6;

interface NewSignUpRendererProps {
  onNext: (schemaName: keyof NewPatientSchemaType) => Promise<void>;
  onRetry?: () => void;
  handleRegistration: () => Promise<void>;
  handleGoBack: (schemaName: keyof NewPatientSchemaType) => void;
  register: UseFormRegister<SignUpPatientSchemaType>;
  setValue: UseFormSetValue<SignUpPatientSchemaType>;
  watch: UseFormWatch<SignUpPatientSchemaType>;
  errors: FieldErrors<NewPatientSchemaType>;
  control?: Control<SignUpPatientSchemaType>;
  isPending?: boolean;
  canResendAt: number;
  setError: UseFormSetError<SignUpPatientSchemaType>;
}
export const NewPatientSignUpRenderer = ({
  onNext,
  onRetry,
  canResendAt,
  handleRegistration,
  handleGoBack,
  register,
  setValue,
  watch,
  errors,
  control,
  isPending = false,
  setError,
}: NewSignUpRendererProps) => {
  const { currentStep } = useOnBoardingNewPatientStore();
  const progressBarStep = currentStep + 1;

  const steps = [
    (totalSteps: number) => (
      <NewPatientNameAndEmailForm
        onNext={() => onNext("namesAndEmail")}
        totalSteps={totalSteps}
        register={(data: keyof NamesAndEmailSchemaType) =>
          register(`namesAndEmail.${data}`)
        }
        errors={errors.namesAndEmail}
        step={progressBarStep}
      />
    ),
    (totalSteps: number) => (
      <NewPatientBirthdateForm
        onNext={() => onNext("date_of_birth")}
        handleGoBack={() => handleGoBack("date_of_birth")}
        totalSteps={totalSteps}
        control={control}
        setValue={setValue}
        dateOfBirth={watch("date_of_birth")}
        errors={errors.date_of_birth}
        step={progressBarStep}
      />
    ),
    (totalSteps: number) => (
      <NewPatientTermsAndConditionsForm
        onNext={() => onNext("termsAndConditions")}
        handleGoBack={() => handleGoBack("termsAndConditions")}
        totalSteps={totalSteps}
        register={(data: keyof TermsAndConditionsSchemaType) =>
          register(`termsAndConditions.${data}`)
        }
        errors={errors.termsAndConditions}
        step={progressBarStep}
        isAdult={isOlderThanYears(
          watch("date_of_birth.birthday"),
          PATIENT_SIGN_UP_ADULT_AGE,
        )}
        currentGuardianName={watch("termsAndConditions.guardian_name")}
        setError={setError}
      />
    ),
    (totalSteps: number) => (
      <PatientAddressForm
        handleGoBack={() => handleGoBack("address")}
        onNext={() => onNext("address")}
        totalSteps={totalSteps}
        register={(data: keyof AddressSchemaType) =>
          register(`address.${data}`)
        }
        errors={errors.address}
        control={control}
        step={progressBarStep}
        addressStateKey="address.state"
      />
    ),
    (totalSteps: number) => (
      <PatientMobileForm
        handleGoBack={() => handleGoBack("phone_number")}
        onNext={() => onNext("phone_number")}
        totalSteps={totalSteps}
        register={register}
        errors={errors.phone_number}
        step={progressBarStep}
      />
    ),
    (totalSteps: number) => (
      <PatientMobileVerificationForm
        handleGoBack={() => handleGoBack("verification")}
        onNext={() => onNext("verification")}
        onRetry={onRetry}
        canResendAt={canResendAt}
        totalSteps={totalSteps}
        control={control}
        errors={errors.verification}
        step={progressBarStep}
        codeFieldKey="verification.code"
        pendingRequest={isPending}
      />
    ),
    (totalSteps: number) => (
      <PatientPasswordForm
        handleGoBack={() => handleGoBack("password")}
        onNext={handleRegistration}
        totalSteps={totalSteps}
        register={(data: keyof PasswordsSchemaType) =>
          register(`password.${data}`)
        }
        errors={errors.password}
        enteredPassword={watch("password.password")}
        pendingRequest={isPending}
        step={progressBarStep}
      />
    ),
  ];

  return steps[currentStep](steps.length);
};
